import { Controller } from "stimulus";
import { enter, leave } from "el-transition";

export default class extends Controller {
  static targets = [
    "background",
    "gradientBackground",
    "whiteBackground",
    "logoLight",
    "logoDark",
    "navText",
    "mobileMenu",
    "mobileMenuButton",
  ];

  connect() {
    console.log("Navbar controller connected");
    this.heroElement = document.querySelector(".hero-section");
    this.isWhiteBackground = false;
    this.mobileMenuOpen = false;
    this.initScrollListener();

    console.log("Initial navTextTargets check:", this.navTextTargets?.length);

    // Add global click handler to close menu when clicking outside
    this.handleOutsideClickBound = this.handleOutsideClick.bind(this);
    document.addEventListener("click", this.handleOutsideClickBound);
  }

  // Clean up event listener on disconnect
  disconnect() {
    document.removeEventListener("click", this.handleOutsideClickBound);
  }

  handleOutsideClick(event) {
    if (
      this.mobileMenuOpen &&
      this.hasMobileMenuTarget &&
      !this.mobileMenuTarget.contains(event.target) &&
      !this.mobileMenuButtonTarget.contains(event.target)
    ) {
      this.toggleMobileMenu();
    }
  }

  toggleMobileMenu(event) {
    if (event) event.preventDefault();

    if (this.mobileMenuOpen) {
      // Close the menu
      leave(this.mobileMenuTarget).then(() => {
        this.mobileMenuTarget.classList.add("hidden");
      });
      this.mobileMenuOpen = false;
    } else {
      // Open the menu
      this.mobileMenuTarget.classList.remove("hidden");
      enter(this.mobileMenuTarget);
      this.mobileMenuOpen = true;
    }
  }

  initScrollListener() {
    // Use a fixed threshold based on navbar height instead of hero height
    this.scrollThreshold = 100; // 100px - approximate navbar height

    // Initial check
    this.checkScroll();

    // Add scroll event listener with throttling for performance
    let lastKnownScrollPosition = 0;
    let ticking = false;

    window.addEventListener("scroll", () => {
      lastKnownScrollPosition = window.scrollY;

      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.checkScroll(lastKnownScrollPosition);
          ticking = false;
        });

        ticking = true;
      }
    });
  }

  checkScroll(scrollPosition = window.scrollY) {
    // No need to recalculate heroHalfwayPoint every time
    // Just use the fixed threshold we set

    if (scrollPosition >= this.scrollThreshold && !this.isWhiteBackground) {
      // Switch to white background
      this.isWhiteBackground = true;
      this.showWhiteBackground();
    } else if (
      scrollPosition < this.scrollThreshold &&
      this.isWhiteBackground
    ) {
      // Switch to gradient background
      this.isWhiteBackground = false;
      this.showGradientBackground();
    }
  }

  showWhiteBackground() {
    console.log("showWhiteBackground called");
    // First hide gradient with el-transition
    leave(this.gradientBackgroundTarget).then(() => {
      this.gradientBackgroundTarget.classList.add("hidden");

      // Then show white background with el-transition
      this.whiteBackgroundTarget.classList.remove("hidden");
      enter(this.whiteBackgroundTarget);

      // Update visual elements
      this.updateElementsForWhiteBackground();
    });
  }

  showGradientBackground() {
    // First hide white background with el-transition
    leave(this.whiteBackgroundTarget).then(() => {
      this.whiteBackgroundTarget.classList.add("hidden");

      // Then show gradient with el-transition
      this.gradientBackgroundTarget.classList.remove("hidden");
      enter(this.gradientBackgroundTarget);

      // Update visual elements
      this.updateElementsForGradientBackground();
    });
  }

  updateElementsForWhiteBackground() {
    // Switch logo if needed
    if (this.hasLogoLightTarget && this.hasLogoDarkTarget) {
      this.logoLightTarget.classList.add("hidden");
      this.logoDarkTarget.classList.remove("hidden");
    }

    // Change text color using direct DOM query as a fallback
    const navTextElements = document.querySelectorAll(
      '[data-target="fixed-nav.navText"]'
    );
    console.log("Nav elements found for white:", navTextElements.length);

    navTextElements.forEach((element) => {
      element.classList.remove("text-white", "hover:text-gray-200");
      element.classList.add("text-gray-800", "hover:text-gray-600");
    });

    console.log("hasMobileMenuButtonTarget: ", this.hasMobileMenuButtonTarget);

    // EXPLICITLY handle mobile menu button color
    if (this.hasMobileMenuButtonTarget) {
      console.log("Updating mobile menu button color for white bg");
      this.mobileMenuButtonTarget.classList.remove("text-white");
      this.mobileMenuButtonTarget.classList.add("text-gray-800");
    }

    // Update mobile menu if open
    if (this.mobileMenuOpen && this.hasMobileMenuTarget) {
      this.mobileMenuTarget.classList.remove("bg-black", "bg-opacity-75");
      this.mobileMenuTarget.classList.add(
        "bg-white",
        "border-t",
        "border-gray-200"
      );
    }
  }

  updateElementsForGradientBackground() {
    // Switch logo if needed
    if (this.hasLogoLightTarget && this.hasLogoDarkTarget) {
      this.logoDarkTarget.classList.add("hidden");
      this.logoLightTarget.classList.remove("hidden");
    }

    // Change text color using direct DOM query as a fallback
    const navTextElements = document.querySelectorAll(
      '[data-target="fixed-nav.navText"]'
    );
    console.log("Nav elements found for gradient:", navTextElements.length);

    navTextElements.forEach((element) => {
      element.classList.remove("text-gray-800", "hover:text-gray-600");
      element.classList.add("text-white", "hover:text-gray-200");
    });

    // EXPLICITLY handle mobile menu button color
    if (this.hasMobileMenuButtonTarget) {
      console.log("Updating mobile menu button color for gradient bg");
      this.mobileMenuButtonTarget.classList.remove("text-gray-800");
      this.mobileMenuButtonTarget.classList.add("text-white");
    }

    // Update mobile menu if open
    if (this.mobileMenuOpen && this.hasMobileMenuTarget) {
      this.mobileMenuTarget.classList.remove(
        "bg-white",
        "border-t",
        "border-gray-200"
      );
      this.mobileMenuTarget.classList.add("bg-black", "bg-opacity-75");
    }
  }
}
